import { auth } from '../firebase';
import {
  ImpactDetectionSettings,
  ImpactDetectionSettingsUpdate,
  ImpactEvent,
  DEFAULT_IMPACT_DETECTION_SETTINGS,
} from '../types/impactDetection';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api`;

interface ImpactEventsResponse {
  events: ImpactEvent[];
}

class ImpactDetectionService {
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    const token = await user.getIdToken();
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }

    return response.json();
  }

  /**
   * Get impact-detection settings for a device
   */
  async getSettings(deviceId: string): Promise<ImpactDetectionSettings> {
    try {
      return await this.request<ImpactDetectionSettings>(
        `/devices/${deviceId}/impact-detection/`
      );
    } catch {
      // Return defaults if settings don't exist yet
      return DEFAULT_IMPACT_DETECTION_SETTINGS;
    }
  }

  /**
   * Update impact-detection settings for a device.
   * Pass actionId: '' to clear the configured action.
   */
  async updateSettings(
    deviceId: string,
    updates: ImpactDetectionSettingsUpdate
  ): Promise<ImpactDetectionSettings> {
    return this.request<ImpactDetectionSettings>(
      `/devices/${deviceId}/impact-detection/`,
      {
        method: 'PUT',
        body: JSON.stringify(updates),
      }
    );
  }

  /**
   * Get the impact event history for a device (newest first)
   */
  async getEvents(deviceId: string, limit: number = 50): Promise<ImpactEvent[]> {
    const response = await this.request<ImpactEventsResponse>(
      `/devices/${deviceId}/impact-detection/events?limit=${limit}`
    );
    return response.events;
  }
}

export const impactDetectionService = new ImpactDetectionService();
